import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = ({ adminKey }) => {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [slots, setSlots] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [slotDate, setSlotDate] = useState('');
  const [slotTime, setSlotTime] = useState('');

  const k = `key=${adminKey}`;

  const fetchDashboard = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/admin/dashboard?${k}`);
    setDashboard(data);
  }, [k]);

  const fetchOrders = useCallback(async (type = '') => {
    const q = type ? `&item_type=${type}` : '';
    const { data } = await axios.get(`${API}/api/admin/orders?${k}${q}`);
    setOrders(data.orders);
  }, [k]);

  const fetchSlots = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/admin/slots?${k}`);
    setSlots(data.slots);
  }, [k]);

  const fetchSubmissions = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/admin/submissions?${k}`);
    setSubmissions(data.submissions);
  }, [k]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (tab === 'orders') fetchOrders(filterType);
    if (tab === 'slots') fetchSlots();
    if (tab === 'submissions') fetchSubmissions();
  }, [tab, filterType, fetchOrders, fetchSlots, fetchSubmissions]);

  const addSlot = async () => {
    if (!slotDate || !slotTime) return;
    await axios.post(`${API}/api/admin/slots?${k}`, { date: slotDate, time: slotTime, duration: 60 });
    setSlotDate(''); setSlotTime('');
    fetchSlots();
  };

  const deleteSlot = async (id) => {
    await axios.delete(`${API}/api/admin/slots/${id}?${k}`);
    fetchSlots();
  };

  const updateOrderStatus = async (orderId, status) => {
    await axios.put(`${API}/api/admin/orders/${orderId}/status?${k}`, { status });
    fetchOrders(filterType);
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'orders', label: 'All Orders' },
    { id: 'merch', label: 'Merch' },
    { id: 'coaching', label: 'Coaching' },
    { id: 'spoilme', label: 'Spoil Me' },
    { id: 'patreon', label: 'Patreon' },
    { id: 'rave', label: 'Rave' },
    { id: 'slots', label: 'Manage Slots' },
    { id: 'submissions', label: 'Questions & Stories' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0208] text-white p-4 md:p-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Syne', sans-serif", color: '#FF00FF' }}>
          Acid Rani Admin
        </h1>
        <p className="text-white/40 text-sm mb-6">Secret dashboard — do not share this URL</p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); if (['merch','coaching','spoilme','patreon','rave'].includes(t.id)) { setFilterType(t.id); setTab('orders'); } else { setFilterType(''); } }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                (tab === t.id || (tab === 'orders' && filterType === t.id))
                  ? 'bg-[#FF00FF] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Dashboard */}
        {tab === 'dashboard' && dashboard && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Orders', value: dashboard.total_orders, color: '#FF00FF' },
                { label: 'Paid', value: dashboard.paid_orders, color: '#CCFF00' },
                { label: 'Merch', value: dashboard.merch_orders, color: '#FF69B4' },
                { label: 'Coaching', value: dashboard.coaching_bookings, color: '#00FFFF' },
                { label: 'Spoil Me', value: dashboard.spoilme_count, color: '#FFD700' },
                { label: 'Patreon', value: dashboard.patreon_count, color: '#FF1493' },
                { label: 'Rave', value: dashboard.rave_count, color: '#8B008B' },
              ].map(s => (
                <div key={s.label} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.color}30` }}>
                  <p className="text-white/40 text-xs uppercase tracking-wider">{s.label}</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
                </div>
              ))}
            </div>
            <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Revenue</p>
              {Object.entries(dashboard.revenue || {}).map(([cur, amt]) => (
                <p key={cur} className="text-lg font-bold" style={{ color: '#CCFF00' }}>
                  {cur === 'INR' ? '₹' : '$'}{amt.toLocaleString()} <span className="text-white/30 text-sm">{cur}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Orders Table */}
        {tab === 'orders' && (
          <div>
            <div className="flex gap-2 mb-4 flex-wrap">
              {['', 'merch', 'coaching', 'spoilme', 'patreon', 'rave'].map(f => (
                <button key={f} onClick={() => setFilterType(f)}
                  className={`px-3 py-1 rounded-full text-xs ${filterType === f ? 'bg-[#FF00FF] text-white' : 'bg-white/5 text-white/50'}`}>
                  {f || 'All'}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {orders.map(o => (
                <div key={o.order_id} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                    <div>
                      <span className="text-white font-bold text-sm">{o.item_name}</span>
                      <span className="text-white/30 text-xs ml-2">{o.order_id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        o.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>{o.payment_status?.toUpperCase()}</span>
                      <select
                        value={o.status || 'pending'}
                        onChange={(e) => updateOrderStatus(o.order_id, e.target.value)}
                        className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-white/50">
                    <p><span className="text-white/30">Type:</span> {o.item_type}</p>
                    <p><span className="text-white/30">Amount:</span> {o.currency} {o.amount}</p>
                    <p><span className="text-white/30">Payment:</span> {o.payment_method || '—'}</p>
                    <p><span className="text-white/30">Date:</span> {o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}</p>
                  </div>

                  {/* Customer Details */}
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-white/40">
                    {o.customer_name && <p><span className="text-white/25">Name:</span> {o.customer_name}</p>}
                    {o.customer_email && <p><span className="text-white/25">Email:</span> {o.customer_email}</p>}
                    {o.phone && <p><span className="text-white/25">Phone:</span> {o.phone}</p>}
                    {o.size && <p><span className="text-white/25">Size:</span> {o.size}</p>}
                    {o.patreon_username && <p><span className="text-white/25">Patreon:</span> {o.patreon_username}</p>}
                    {o.contact_method && <p><span className="text-white/25">{o.contact_method}:</span> {o.contact_value}</p>}
                  </div>

                  {/* Address */}
                  {o.address_line1 && (
                    <p className="mt-1 text-xs text-white/30">
                      {[o.address_line1, o.address_line2, o.city, o.state, o.pincode, o.country].filter(Boolean).join(', ')}
                    </p>
                  )}

                  {/* Message */}
                  {o.message && (
                    <div className="mt-2 p-2 rounded-lg" style={{ background: 'rgba(255,0,255,0.05)', border: '1px solid rgba(255,0,255,0.1)' }}>
                      <p className="text-xs text-white/30 mb-1">Message:</p>
                      <p className="text-sm text-[#FFD1DC]">{o.message}</p>
                    </div>
                  )}

                  {o.extra_details && (
                    <div className="mt-2 p-2 rounded-lg" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.1)' }}>
                      <p className="text-xs text-white/30 mb-1">Extra Details:</p>
                      <p className="text-sm text-[#FFD1DC]">{o.extra_details}</p>
                    </div>
                  )}
                </div>
              ))}
              {orders.length === 0 && <p className="text-white/30 text-center py-8">No orders found</p>}
            </div>
          </div>
        )}

        {/* Manage Slots */}
        {tab === 'slots' && (
          <div>
            <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,255,255,0.2)' }}>
              <h3 className="text-lg font-bold mb-3" style={{ color: '#00FFFF' }}>Add Coaching Slot</h3>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-white/40 text-xs block mb-1">Date</label>
                  <input type="date" value={slotDate} onChange={e => setSlotDate(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Time (IST)</label>
                  <input type="time" value={slotTime} onChange={e => setSlotTime(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <button onClick={addSlot}
                  className="px-6 py-2 rounded-lg bg-[#00FFFF] text-[#0A0208] font-bold text-sm hover:opacity-90">
                  Add Slot
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {slots.map(s => (
                <div key={s.slot_id} className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.status === 'booked' ? 'rgba(255,0,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-white font-bold text-sm">{s.date}</p>
                      <p className="text-white/50 text-xs">{s.time} IST · {s.duration}min</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      s.status === 'booked' ? 'bg-[#FF00FF]/20 text-[#FF00FF]' : 'bg-green-500/20 text-green-400'
                    }`}>{s.status?.toUpperCase()}</span>
                    {s.booked_by && <span className="text-xs text-white/30">{s.booked_by}</span>}
                  </div>
                  {s.status === 'open' && (
                    <button onClick={() => deleteSlot(s.slot_id)}
                      className="text-red-400 text-xs hover:text-red-300">Remove</button>
                  )}
                </div>
              ))}
              {slots.length === 0 && <p className="text-white/30 text-center py-8">No slots created yet. Add coaching slots above.</p>}
            </div>
          </div>
        )}

        {/* Submissions — Questions, Stories, Kinks */}
        {tab === 'submissions' && (
          <div>
            <p className="text-white/40 text-sm mb-4">Questions, stories, and fantasies from your community</p>
            <div className="space-y-3">
              {submissions.map(s => (
                <div key={s.submission_id} className="p-4 rounded-xl"
                  style={{
                    background: s.submission_type === 'question' ? 'rgba(255,0,255,0.04)' : s.submission_type === 'story' ? 'rgba(0,255,255,0.04)' : 'rgba(204,255,0,0.04)',
                    border: `1px solid ${s.submission_type === 'question' ? 'rgba(255,0,255,0.15)' : s.submission_type === 'story' ? 'rgba(0,255,255,0.15)' : 'rgba(204,255,0,0.15)'}`,
                  }}>
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase ${
                      s.submission_type === 'question' ? 'bg-[#FF00FF]/20 text-[#FF00FF]' :
                      s.submission_type === 'story' ? 'bg-[#00FFFF]/20 text-[#00FFFF]' : 'bg-[#CCFF00]/20 text-[#CCFF00]'
                    }`}>{s.submission_type}</span>
                    <span className="text-white/20 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  <p className="text-[#FFD1DC] text-sm leading-relaxed mb-2">{s.content}</p>
                  <div className="flex gap-4 text-xs text-white/30">
                    <span>From: {s.name || 'Anonymous'}</span>
                    {s.instagram && <span>IG: {s.instagram}</span>}
                  </div>
                </div>
              ))}
              {submissions.length === 0 && <p className="text-white/30 text-center py-8">No submissions yet.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
